import React, { useEffect, useMemo, useState } from "react";
import { Ic, StatusBadge, Modal, inputStyle, btnPrimary, btnSecondary, btnGhost, DOMAIN_COLORS, showStyledAlert, showStyledConfirm } from "../components/UI";
import { addDomain, updateDomain, deleteDomain } from "../utils/firestore";

/* ═══════════════════════════════════════════
   INJECT STYLES
   ═══════════════════════════════════════════ */

if (typeof document !== "undefined") {
  const id = "domains-page-styles";
  if (!document.getElementById(id)) {
    const s = document.createElement("style");
    s.id = id;
    s.textContent = `
      @keyframes domFadeIn {
        from { opacity: 0; transform: translateY(4px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      .dom-row { transition: background .12s ease; }
      .dom-row:hover { background: #F3F4FF !important; }
      .dom-row:hover .dom-actions { opacity: 1; }
      .dom-row-main:hover { background: #EEEEFF22; }
      .dom-sub-chip { transition: all .1s ease; }
      .dom-sub-chip:hover { background: #EEEEF2 !important; transform: scale(1.04); }
      .dom-color-dot { transition: transform .12s ease; }
      .dom-color-dot:hover { transform: scale(1.15); }
    `;
    document.head.appendChild(s);
  }
}

/* ═══════════════════════════════════════════
   SHARED STYLES
   ═══════════════════════════════════════════ */

const selectStyle = {
  ...inputStyle,
  borderRadius: 10,
  padding: "8px 32px 8px 12px",
  fontSize: 13,
  cursor: "pointer",
  appearance: "none",
  backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%238E8EA0' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "left 10px center",
  fontFamily: "inherit",
};

const labelStyle = {
  display: "block", fontSize: 13, fontWeight: 600, color: "#525264", marginBottom: 6,
};

/* ═══════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════ */

export default function DomainsPage({ domains, activities, onRefresh, showToast, isMobile = false, canEditDomains = true }) {
  const [modal, setModal] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewMode, setViewMode] = useState("main_only"); // all | main_only
  const [mainCategoryFilter, setMainCategoryFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);

  const monthAgo = new Date();
  monthAgo.setDate(monthAgo.getDate() - 30);
  const isMainCategory = (d) => d?.isMainCategory !== false;
  const domainById = useMemo(() => {
    const m = {};
    domains.forEach((d) => { m[d.id] = d; });
    return m;
  }, [domains]);
  const childDomainsByMainId = useMemo(() => {
    const m = {};
    domains.forEach((d) => {
      const parentId = (d.parentDomainId || "").trim();
      if (!parentId) return;
      if (!m[parentId]) m[parentId] = [];
      m[parentId].push(d);
    });
    Object.keys(m).forEach((k) => {
      m[k].sort((a, b) => (a.name || "").localeCompare((b.name || ""), "he"));
    });
    return m;
  }, [domains]);

  const toDateSafe = (value) => {
    if (!value) return null;
    const raw = String(value).trim();
    if (!raw) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const d = new Date(`${raw}T00:00:00`);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const domainStatsById = useMemo(() => {
    const map = {};
    domains.forEach((d) => {
      map[d.id] = {
        monthlyHours: 0,
        totalHours: 0,
        monthlyActivities: 0,
        totalActivities: 0,
      };
    });

    (activities || []).forEach((a) => {
      const domainId = (a?.domainId || "").trim();
      if (!domainId || !map[domainId]) return;
      const duration = Number(a?.duration || 0);
      const safeDuration = Number.isFinite(duration) ? duration : 0;
      const date = toDateSafe(a?.date);
      map[domainId].totalHours += safeDuration;
      map[domainId].totalActivities += 1;
      if (date && date >= monthAgo) {
        map[domainId].monthlyHours += safeDuration;
        map[domainId].monthlyActivities += 1;
      }
    });

    return map;
  }, [activities, domains, monthAgo]);

  const getDomainStats = (domain) => {
    const own = domainStatsById[domain.id] || {
      monthlyHours: 0,
      totalHours: 0,
      monthlyActivities: 0,
      totalActivities: 0,
    };
    if (!isMainCategory(domain)) return own;

    const children = childDomainsByMainId[domain.id] || [];
    return children.reduce((acc, child) => {
      const s = domainStatsById[child.id] || {};
      acc.monthlyHours += Number(s.monthlyHours || 0);
      acc.totalHours += Number(s.totalHours || 0);
      acc.monthlyActivities += Number(s.monthlyActivities || 0);
      acc.totalActivities += Number(s.totalActivities || 0);
      return acc;
    }, { ...own });
  };

  const handleSave = async (form) => {
    if (!canEditDomains) {
      await showStyledAlert("אין הרשאה לעריכת תחומים.");
      return;
    }
    try {
      const basePayload = { ...form, city: "" };
      if (modal === "add") {
        await addDomain(basePayload);
        showToast("תחום נוסף");
      } else {
        await updateDomain(modal.d.id, basePayload);
        showToast("עודכן");
      }
      setModal(null);
      onRefresh();
    } catch (e) {
      await showStyledAlert(e.message);
    }
  };

  const toggleMainCategory = async (d) => {
    if (!canEditDomains) {
      await showStyledAlert("אין הרשאה לעריכת תחומים.");
      return;
    }
    const currentlyMain = isMainCategory(d);
    if (currentlyMain) {
      await showStyledAlert("כדי להגדיר כמשני, ערוך את התחום ובחר קטגוריה ראשית אב.");
      return;
    }
    try {
      await updateDomain(d.id, { isMainCategory: true, parentDomainId: "" });
      showToast("הוגדר כקטגוריה ראשית");
      onRefresh();
    } catch (e) {
      await showStyledAlert(e.message);
    }
  };

  const toggleActive = async (d) => {
    if (!canEditDomains) {
      await showStyledAlert("אין הרשאה לעריכת תחומים.");
      return;
    }
    try {
      await updateDomain(d.id, { active: !d.active });
      showToast(d.active ? "הושבת" : "הופעל");
      onRefresh();
    } catch (e) {
      await showStyledAlert(e.message);
    }
  };

  const removeDomain = async (d) => {
    if (!canEditDomains) {
      await showStyledAlert("אין הרשאה לעריכת תחומים.");
      return;
    }
    const linkedActivities = activities.filter((a) => a.domainId === d.id).length;
    const message = linkedActivities > 0
      ? `לתחום "${d.name}" משויכות ${linkedActivities} פעילויות. למחוק בכל זאת?`
      : `למחוק את התחום "${d.name}"?`;
    if (!await showStyledConfirm(message)) return;
    try {
      await deleteDomain(d.id);
      showToast("התחום נמחק");
      onRefresh();
    } catch (e) {
      await showStyledAlert(e.message);
    }
  };

  const removeChildDomainGroup = async (parentDomain, childName, groupItems) => {
    if (!canEditDomains) {
      await showStyledAlert("אין הרשאה לעריכת תחומים.");
      return;
    }
    const items = Array.isArray(groupItems) ? groupItems : [];
    if (!items.length) return;
    const linkedActivities = activities.filter((a) => items.some((it) => it.id === a.domainId)).length;
    const msg = linkedActivities > 0
      ? `לתת־התחום "${childName}" יש ${items.length} רשומות ו-${linkedActivities} פעילויות משויכות. למחוק את כולן?`
      : `למחוק את תת־התחום "${childName}"?`;
    if (!await showStyledConfirm(msg)) return;
    try {
      for (const item of items) {
        await deleteDomain(item.id);
      }
      showToast(`נמחקו ${items.length} פריטים מתוך "${childName}"`);
      onRefresh();
    } catch (e) {
      await showStyledAlert(e.message);
    }
  };

  const mainCategories = useMemo(
    () => domains.filter((d) => isMainCategory(d)).sort((a, b) => (a.name || "").localeCompare((b.name || ""), "he")),
    [domains]
  );
  const childDomainCountByMainId = useMemo(() => {
    const counts = {};
    domains.forEach((d) => {
      const parentId = (d.parentDomainId || "").trim();
      if (!parentId) return;
      const nameKey = (d.name || "").trim().toLowerCase();
      if (!nameKey) return;
      if (!counts[parentId]) counts[parentId] = new Set();
      counts[parentId].add(nameKey);
    });
    return Object.fromEntries(
      Object.entries(counts).map(([parentId, items]) => [parentId, items.size])
    );
  }, [domains]);

  useEffect(() => {
    if (mainCategoryFilter === "all") return;
    if (!domains.some((d) => d.id === mainCategoryFilter && isMainCategory(d))) {
      setMainCategoryFilter("all");
    }
    return undefined;
  }, [domains, mainCategoryFilter]);

  const filteredDomains = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    return domains.filter((d) => {
      if (viewMode === "main_only") {
        if (!isMainCategory(d)) return false;
        if (mainCategoryFilter !== "all" && d.id !== mainCategoryFilter) return false;
      }
      if (statusFilter === "active" && !d.active) return false;
      if (statusFilter === "inactive" && d.active) return false;
      if (!q) {
        return true;
      }
      const parentName = domainById[d.parentDomainId || ""]?.name || "";
      const hay = [d.name || "", parentName, d.city || ""].join(" ").toLowerCase();
      if (hay.includes(q)) return true;
      if (viewMode === "main_only") {
        const children = childDomainsByMainId[d.id] || [];
        return children.some((c) => {
          const childHay = [c.name || "", c.city || ""].join(" ").toLowerCase();
          return childHay.includes(q);
        });
      }
      return false;
    });
  }, [domains, search, statusFilter, domainById, viewMode, mainCategoryFilter, childDomainsByMainId]);

  /* Stats */
  const activeCount = domains.filter((d) => d.active).length;
  const totalSubs = Object.values(childDomainCountByMainId).reduce((sum, count) => sum + Number(count || 0), 0);

  return (
    <div style={{ fontFamily: "'Heebo', 'Assistant', sans-serif", direction: "rtl" }}>

      {/* ════════ Header card ════════ */}
      <div style={{
        background: "#fff", borderRadius: 16,
        border: "1px solid #E8E8ED",
        padding: "16px 20px",
        marginBottom: 14,
        boxShadow: "0 1px 4px rgba(0,0,0,.03)",
      }}>
        {/* Title row */}
        <div style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "center", gap: 12,
          marginBottom: 14, flexWrap: "wrap",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ fontSize: 20, fontWeight: 800, fontFamily: "Assistant", color: "#1A1A2E" }}>
              ניהול תחומים
            </div>
            <span style={{
              fontSize: 12, fontWeight: 600,
              padding: "3px 10px", borderRadius: 8,
              background: "#F5F5F7", color: "#6B6B80",
            }}>
              {activeCount} פעילים · {totalSubs} תתי תחום
            </span>
          </div>
          {canEditDomains && <button onClick={() => setModal("add")} style={{
            ...btnPrimary, borderRadius: 12, padding: "9px 20px",
            fontSize: 14, fontWeight: 600,
            display: "inline-flex", alignItems: "center", gap: 6,
            boxShadow: "0 2px 8px rgba(196,30,58,.18)",
          }}>
            <Ic.Plus /> תחום חדש
          </button>}
        </div>

        {/* Filters row */}
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr auto auto",
          gap: 8,
          alignItems: "center",
        }}>
          <div style={{ position: "relative" }}>
            <input
              style={{
                ...inputStyle,
                borderRadius: 10,
                padding: "8px 36px 8px 12px",
                fontSize: 13,
                width: "100%",
              }}
              placeholder="חיפוש לפי קטגוריה או תת קטגוריה..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <span style={{
              position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
              fontSize: 14, color: "#A3A3B5", pointerEvents: "none",
            }}>🔍</span>
          </div>
          <select style={selectStyle} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">כל הסטטוסים</option>
            <option value="active">פעיל</option>
            <option value="inactive">לא פעיל</option>
          </select>
          <select style={selectStyle} value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
            <option value="all">תצוגה: כל התחומים</option>
            <option value="main_only">תצוגה: קטגוריות ראשיות</option>
          </select>
          {viewMode === "main_only" && (
            <select style={selectStyle} value={mainCategoryFilter} onChange={(e) => setMainCategoryFilter(e.target.value)}>
              <option value="all">כל הקטגוריות הראשיות</option>
              {mainCategories.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* ════════ Domains table / cards ════════ */}
      <div style={{
        background: "#fff", borderRadius: 16,
        border: "1px solid #E8E8ED",
        overflowX: isMobile ? "auto" : "hidden",
        overflowY: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,.03)",
      }}>
        {/* Table header */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "4px 1fr 140px 100px 100px 140px 124px",
          background: "#FAFAFC",
          borderBottom: "1px solid #E8E8ED",
          padding: "10px 0",
          minWidth: 640,
        }}>
            <div />
            {["תחום", "קטגוריה", "חודשי", "מצטבר", "תתי קטגוריות", ""].map((h) => (
              <div key={h} style={{
                padding: "0 14px", fontSize: 12, fontWeight: 600,
                color: "#8E8EA0", textAlign: "right",
              }}>{h}</div>
            ))}
          </div>

        {/* Rows */}
        {filteredDomains.map((d, idx) => {
          const stats = getDomainStats(d);
          const mh = Number(stats.monthlyHours || 0);
          const th = Number(stats.totalHours || 0);
          const monthlyActivitiesCount = Number(stats.monthlyActivities || 0);
          const totalActivitiesCount = Number(stats.totalActivities || 0);
          const childDomains = childDomainsByMainId[d.id] || [];
          const isExpanded = expandedId === d.id;

          return (
            <div
              key={d.id}
              className="dom-row"
              style={{
                borderBottom: "1px solid #F3F3F6",
                animation: `domFadeIn .2s ease ${Math.min(idx * 0.03, 0.3)}s both`,
              }}
            >
              {/* Main row */}
              <div
                onClick={() => setExpandedId(isExpanded ? null : d.id)}
                style={{
                  display: "grid",
                  gridTemplateColumns: "4px 1fr 140px 100px 100px 140px 124px",
                  alignItems: "center",
                  minHeight: 56,
                  minWidth: 640,
                  cursor: "pointer",
                }}
              >
                {/* Color strip */}
                <div style={{ background: d.color, height: "100%", width: 4 }} />

                {/* Name + status */}
                <div style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    width: 10, height: 10, borderRadius: 4,
                    background: d.color,
                    boxShadow: `0 0 0 2px ${d.color}25`,
                    flexShrink: 0,
                  }} />
                  <div>
                    <div style={{
                      fontSize: 14, fontWeight: 700, color: "#1A1A2E",
                      display: "flex", alignItems: "center", gap: 8,
                    }}>
                      {d.name}
                      <span style={{
                        fontSize: 10, fontWeight: 600,
                        padding: "2px 8px", borderRadius: 6,
                        background: d.active ? "#ECFDF5" : "#F5F5F7",
                        color: d.active ? "#059669" : "#8E8EA0",
                      }}>
                        {d.active ? "פעיל" : "מושבת"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Category info */}
                <div style={{ padding: "10px 14px" }}>
                  {isMainCategory(d) ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleMainCategory(d); }}
                        style={{
                          border: "1px solid #BBF7D0",
                          background: "#ECFDF5",
                          color: "#065F46",
                          borderRadius: 8,
                          padding: "4px 10px",
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        קטגוריה ראשית
                      </button>
                      <span style={{ fontSize: 11, color: "#6B7280" }}>
                        {(childDomainCountByMainId[d.id] || 0)} תתי קטגוריות
                      </span>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleMainCategory(d); }}
                        style={{
                          border: "1px solid #E5E7EB",
                          background: "#fff",
                          color: "#374151",
                          borderRadius: 8,
                          padding: "4px 10px",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        קטגוריה משנית
                      </button>
                      <span style={{ fontSize: 11, color: "#6B7280" }}>
                        אב: {domainById[d.parentDomainId || ""]?.name || "לא הוגדר"}
                      </span>
                    </div>
                  )}
                </div>

                {/* Monthly hours */}
                <div style={{ padding: "10px 14px", textAlign: "right" }}>
                  <span style={{ fontSize: 16, fontWeight: 800, color: d.color }}>{mh.toFixed(0)}</span>
                  <div style={{ fontSize: 10, color: "#8E8EA0" }}>שעות</div>
                  <div style={{ fontSize: 10, color: "#6B7280" }}>{monthlyActivitiesCount} פעילויות</div>
                </div>

                {/* Total hours */}
                <div style={{ padding: "10px 14px", textAlign: "right" }}>
                  <span style={{ fontSize: 16, fontWeight: 800, color: "#2D2D3F" }}>{th.toFixed(0)}</span>
                  <div style={{ fontSize: 10, color: "#8E8EA0" }}>שעות</div>
                  <div style={{ fontSize: 10, color: "#6B7280" }}>{totalActivitiesCount} פעילויות</div>
                </div>

                {/* Subdomains count / chips */}
                <div style={{ padding: "10px 14px" }}>
                  {viewMode === "main_only" && isMainCategory(d) ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "#525264", fontWeight: 500 }}>
                      <span style={{
                        background: "#EEF2FF", borderRadius: 6,
                        padding: "2px 8px", fontSize: 12, fontWeight: 700,
                        color: "#3730A3",
                      }}>{childDomainCountByMainId[d.id] || 0}</span>
                      קטגוריות משניות
                      <span style={{
                        transform: isExpanded ? "rotate(180deg)" : "rotate(0)",
                        transition: "transform .15s ease",
                        fontSize: 10,
                      }}>▼</span>
                    </div>
                  ) : (
                    <span style={{ fontSize: 12, color: "#B0B0C0" }}>—</span>
                  )}
                </div>

                {/* Actions */}
                <div className="dom-actions" style={{
                  padding: "10px 14px",
                  display: "flex", gap: 4,
                  opacity: 1,
                  transition: "opacity .15s ease",
                }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); setModal({ t: "edit", d }); }}
                    title="עריכה"
                    style={{
                      ...btnGhost, width: 32, height: 32, borderRadius: 8,
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                    }}
                  ><Ic.Edit /></button>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleActive(d); }}
                    title={d.active ? "השבת" : "הפעל"}
                    style={{
                      ...btnGhost, width: 32, height: 32, borderRadius: 8,
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      color: d.active ? "#EA580C" : "#059669",
                    }}
                  >
                    {d.active ? <Ic.X /> : <Ic.Check />}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeDomain(d); }}
                    title="מחיקה"
                    style={{
                      ...btnGhost, width: 32, height: 32, borderRadius: 8,
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      color: "#DC2626",
                    }}
                  >
                    <Ic.Trash />
                  </button>
                </div>
              </div>

              {/* Expanded subdomains row */}
              {isExpanded && viewMode === "main_only" && isMainCategory(d) && (
                <div style={{
                  padding: "10px 14px 14px 14px",
                  marginRight: 18,
                  display: "grid",
                  gap: 8,
                  background: "#FBFBFE",
                  borderTop: "1px dashed #E5E7EB",
                  animation: "domFadeIn .15s ease",
                }}>
                  {childDomains.length === 0 && (
                    <div style={{ fontSize: 12, color: "#8E8EA0", padding: "4px 8px" }}>
                      אין קטגוריות משניות משויכות לקטגוריה זו.
                    </div>
                  )}
                  {Object.values(
                    childDomains.reduce((acc, child) => {
                      const key = (child.name || "").trim().toLowerCase() || child.id;
                      if (!acc[key]) acc[key] = { name: child.name || "ללא שם", items: [] };
                      acc[key].items.push(child);
                      return acc;
                    }, {})
                  )
                    .sort((a, b) => (a.name || "").localeCompare((b.name || ""), "he"))
                    .map((group) => {
                      const groupItems = [...group.items].sort((a, b) => {
                        const cityA = (a.city || "").trim();
                        const cityB = (b.city || "").trim();
                        if (cityA && cityB) return cityA.localeCompare(cityB, "he");
                        if (cityA) return -1;
                        if (cityB) return 1;
                        return (a.id || "").localeCompare(b.id || "");
                      });
                      const groupStats = groupItems.reduce((acc, item) => {
                        const s = getDomainStats(item);
                        acc.monthlyHours += Number(s.monthlyHours || 0);
                        acc.totalHours += Number(s.totalHours || 0);
                        acc.monthlyActivities += Number(s.monthlyActivities || 0);
                        acc.totalActivities += Number(s.totalActivities || 0);
                        return acc;
                      }, { monthlyHours: 0, totalHours: 0, monthlyActivities: 0, totalActivities: 0 });
                      return (
                        <div key={`${d.id}__${(group.name || "").trim().toLowerCase()}`} style={{
                          border: "1px solid #E8E8ED",
                          borderRadius: 12,
                          background: "white",
                          overflow: "hidden",
                        }}>
                          <div style={{
                            display: "grid",
                            gridTemplateColumns: "1fr auto auto auto",
                            gap: 10,
                            alignItems: "center",
                            padding: 10,
                            background: "#FCFCFF",
                          }}>
                            <div>
                              <div style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 8,
                                fontWeight: 700,
                                color: "#1A1A2E",
                              }}>
                                <span style={{ width: 8, height: 8, borderRadius: 999, background: groupItems[0]?.color || d.color }} />
                                <span>{group.name}</span>
                              </div>
                              <div style={{ marginTop: 4, fontSize: 11, color: "#6B7280" }}>
                                תת־תחום
                              </div>
                            </div>
                            <div style={{ textAlign: "right", minWidth: 90 }}>
                              <div style={{ fontSize: 14, fontWeight: 800, color: groupItems[0]?.color || d.color }}>
                                {groupStats.monthlyHours.toFixed(0)}
                              </div>
                              <div style={{ fontSize: 10, color: "#6B7280" }}>
                                חודשי · {groupStats.monthlyActivities} פע'
                              </div>
                            </div>
                            <div style={{ textAlign: "right", minWidth: 90 }}>
                              <div style={{ fontSize: 14, fontWeight: 800, color: "#111827" }}>
                                {groupStats.totalHours.toFixed(0)}
                              </div>
                              <div style={{ fontSize: 10, color: "#6B7280" }}>
                                מצטבר · {groupStats.totalActivities} פע'
                              </div>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
                              <button
                                onClick={() => setModal({ t: "edit", d: groupItems[0] })}
                                title="עריכת תת־תחום"
                                style={{
                                  ...btnGhost, width: 32, height: 32, borderRadius: 8,
                                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                                  border: "1px solid #E5E7EB", background: "#fff",
                                }}
                              >
                                <Ic.Edit />
                              </button>
                              <button
                                onClick={() => removeChildDomainGroup(d, group.name, groupItems)}
                                title="מחיקת תת־תחום"
                                style={{
                                  ...btnGhost, width: 32, height: 32, borderRadius: 8,
                                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                                  color: "#B91C1C", border: "1px solid #FECACA", background: "#FEF2F2",
                                }}
                              >
                                <Ic.Trash />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          );
        })}

        {/* Empty state */}
        {filteredDomains.length === 0 && (
          <div style={{
            padding: "48px 20px",
            textAlign: "center", color: "#A3A3B5",
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: "50%",
              background: "#F5F5F7",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              marginBottom: 10, fontSize: 20,
            }}>📂</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#525264", marginBottom: 4 }}>
              לא נמצאו תחומים
            </div>
            <div style={{ fontSize: 13 }}>נסה לשנות את הסינון</div>
          </div>
        )}
      </div>

      {/* ════════ Modal ════════ */}
      {(modal === "add" || modal?.t === "edit") && (
        <DomFormModal dom={modal?.d} domains={domains} onClose={() => setModal(null)} onSave={handleSave} />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   FORM MODAL
   ═══════════════════════════════════════════ */

function DomFormModal({ dom, domains = [], onClose, onSave }) {
  const mainDomainOptions = useMemo(
    () => domains.filter((d) => d.id !== dom?.id && d.isMainCategory !== false),
    [domains, dom?.id]
  );
  const [f, setF] = useState({
    name: dom?.name || "",
    color: dom?.color || "#C41E3A",
    active: dom?.active ?? true,
    isMainCategory: dom?.isMainCategory !== false,
    parentDomainId: dom?.parentDomainId || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!f.isMainCategory && !f.parentDomainId) {
      await showStyledAlert("יש לבחור קטגוריה ראשית אב עבור תחום משני.");
      return;
    }
    setSaving(true);
    await onSave({
      name: f.name,
      color: f.color,
      active: f.active,
      isMainCategory: f.isMainCategory,
      parentDomainId: f.isMainCategory ? "" : f.parentDomainId,
      city: "",
      subdomains: [],
    });
    setSaving(false);
  };

  return (
    <Modal
      title={dom ? "עריכת תחום" : "תחום חדש"}
      onClose={onClose}
      footer={
        <>
          <button
            onClick={submit}
            disabled={saving || !f.name}
            style={{
              ...btnPrimary,
              borderRadius: 12,
              padding: "9px 20px",
              fontSize: 14,
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              ...(!f.name && { opacity: 0.5, pointerEvents: "none" }),
            }}
          >
            <Ic.Check />
            {saving ? "שומר..." : "שמירה"}
          </button>
          <button onClick={onClose} style={{
            ...btnSecondary, borderRadius: 12, padding: "9px 16px", fontSize: 13,
          }}>ביטול</button>
        </>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>שם התחום</label>
        <input
          style={{ ...inputStyle, borderRadius: 10, padding: "9px 14px", fontSize: 14 }}
          value={f.name}
          onChange={(e) => setF((p) => ({ ...p, name: e.target.value }))}
          autoFocus
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>צבע</label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {DOMAIN_COLORS.map((c) => (
            <div
              key={c}
              className="dom-color-dot"
              onClick={() => setF((p) => ({ ...p, color: c }))}
              style={{
                width: 34, height: 34, borderRadius: 10,
                background: c, cursor: "pointer",
                border: f.color === c ? "3px solid #1A1A2E" : "3px solid transparent",
                boxShadow: f.color === c ? "0 0 0 2px white, 0 0 0 4px #1A1A2E" : "none",
                transition: "box-shadow .12s ease",
              }}
            />
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600, color: "#374151" }}>
          <input
            type="checkbox"
            checked={f.isMainCategory}
            onChange={(e) => setF((p) => ({ ...p, isMainCategory: e.target.checked, parentDomainId: e.target.checked ? "" : p.parentDomainId }))}
          />
          קטגוריה ראשית
        </label>
      </div>

      {!f.isMainCategory && (
        <>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>קטגוריה ראשית אב</label>
            <select
              style={{ ...inputStyle, borderRadius: 10, padding: "9px 14px", fontSize: 14, cursor: "pointer" }}
              value={f.parentDomainId}
              onChange={(e) => setF((p) => ({ ...p, parentDomainId: e.target.value }))}
            >
              <option value="">בחר קטגוריה ראשית</option>
              {mainDomainOptions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
        </>
      )}
    </Modal>
  );
}
